import { Link } from "react-router-dom";
import { SmartImage, type FocalPoint } from "@/components/SmartImage";

interface ExcursionCardProps {
  id: string;
  title: string;
  category?: string;
  cover_image?: string;
  image?: string;
  tags?: any;
  resume?: string;
  debut?: string;
  duree?: string;
  prix_eur?: number;
  participants_min?: number;
  participants_max?: number;
  niveau?: number;
  original_image?: string;
  whatsapp_catalog_id?: string;
  focal_point?: FocalPoint | null;
  /** Cadre vertical 4/5 (orienté mobile-first) au lieu de 4/3 → 3/2 */
  orientation?: "horizontal" | "vertical";
}

/**
 * Editorial card · image with integrated title overlay.
 * Less vertical (3/2 ratio), title baked into the visual.
 */
export const ExcursionCard = ({
  id,
  title,
  cover_image,
  image,
  tags,
  focal_point,
  duree,
  niveau,
  orientation = "vertical",
}: ExcursionCardProps) => {
  const src = cover_image || image;
  const aspect = orientation === "vertical"
    ? "aspect-[4/5] sm:aspect-[3/2]"
    : "aspect-[4/3] sm:aspect-[3/2]";

  return (
    <Link
      to={`/excursions/${id}`}
      className="group block focus-visible-ring rounded-xl hover-tilt"
      aria-label={`Voir les détails de l'excursion ${title}`}
    >
      <div className="relative overflow-hidden rounded-xl bg-muted">
        <SmartImage
          src={src}
          alt={title}
          aspectClassName={aspect}
          className="transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          focalPoint={focal_point ?? undefined}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent pointer-events-none" />

        {/* Tags */}
        {(tags?.coup_de_coeur || tags?.impact_solidaire) && (
          <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 z-10">
            {tags?.coup_de_coeur && (
              <span className="bg-primary/95 text-primary-foreground text-[10px] uppercase tracking-wider font-medium px-2.5 py-1 rounded-full backdrop-blur-sm">
                Coup de cœur
              </span>
            )}
            {tags?.impact_solidaire && (
              <span className="bg-emerald-600/95 text-white text-[10px] uppercase tracking-wider font-medium px-2.5 py-1 rounded-full backdrop-blur-sm">
                Solidaire
              </span>
            )}
          </div>
        )}

        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5 transition-transform duration-500 group-hover:translate-y-[-2px]">
          {(duree || niveau) && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2 text-[10px] uppercase tracking-wider text-white/85 font-medium">
              {duree && <span>⏱ {duree}</span>}
              {niveau ? <span>· Niveau {niveau}/5</span> : null}
            </div>
          )}
          <h3 className="font-league uppercase text-white text-2xl sm:text-3xl md:text-[28px] tracking-tight leading-[0.95] drop-shadow-md line-clamp-2">
            {title}
          </h3>
        </div>
      </div>
    </Link>
  );
};
